/**
 * Context Extraction Utilities
 *
 * Functions for extracting consumer info, flow outcomes, OCR data,
 * and other relevant information from machine traces.
 */

import type { MachineTrace } from "../trace-inspector";
import type {
  CapturedConsumerInfo,
  CapturedIntakeResponses,
  CapturedLicenseOcrData,
  CapturedOcrData,
  FlowOutcome,
  OcrOutcome,
} from "./types";

// ============================================================================
// Context Reconstruction
// ============================================================================

/**
 * Reconstruct final context by applying deltas to initial context
 */
export function reconstructFinalContext(
  trace: MachineTrace,
): Record<string, unknown> {
  let context = { ...trace.initialContext };

  for (const event of trace.events) {
    if (event.contextDelta) {
      const delta = event.contextDelta;

      // Apply added
      for (const [key, value] of Object.entries(delta.added)) {
        context[key] = value;
      }

      // Apply changed
      for (const [key, change] of Object.entries(delta.changed)) {
        context[key] = change.to;
      }

      // Apply removed
      for (const key of delta.removed) {
        delete context[key];
      }
    }

    // If there's a full snapshot, use it
    if (event.contextSnapshot) {
      context = { ...event.contextSnapshot };
    }
  }

  return context;
}

// ============================================================================
// OCR Data Extraction
// ============================================================================

/**
 * Extract OCR data from trace context
 * Looks for ocrData in context deltas (saved by saveOcrToContext action)
 */
export function extractOcrData(trace: MachineTrace): CapturedOcrData | null {
  // Walk through events to find ocrData in context deltas
  for (const event of trace.events) {
    if (event.contextDelta?.added?.ocrData) {
      const ocrData = event.contextDelta.added.ocrData as CapturedOcrData;
      if (ocrData.success) {
        return ocrData;
      }
    }
    // Also check 'changed' in case it was updated
    if (event.contextDelta?.changed?.ocrData) {
      const change = event.contextDelta.changed.ocrData as {
        to: CapturedOcrData;
      };
      if (change.to?.success) {
        return change.to;
      }
    }
  }
  return null;
}

export function extractInsuranceOcrData(
  trace: MachineTrace,
): CapturedLicenseOcrData | null {
  // Walk through events to find insuranceOcrData in context deltas
  for (const event of trace.events) {
    if (event.contextDelta?.added?.insuranceOcrData) {
      const ocrData = event.contextDelta.added
        .ocrPrimaryInsurance as CapturedLicenseOcrData;
      if (ocrData.success) {
        return ocrData;
      }
    }
  }
  return null;
}

/**
 * Detect OCR outcome from state path and extract captured data
 * Determines if the flow went through OCR and whether it succeeded or failed.
 * Used to select the appropriate test video file for Playwright's fake camera
 * and to mock the OCR API response with captured data.
 *
 * State paths use dot notation for nested states (e.g., "ocrLicense.licenseFrontPhoto")
 *
 * License OCR flow:
 * - Success: statePath includes "ocrLicense.saveOcrToContext" (OCR processed successfully)
 * - Failure: statePath includes "ocrLicense.ocrIdError" or skipped directly to manual entry
 *
 * Insurance OCR flow:
 * - Success: statePath includes "*.saveInsuranceOcrToContext"
 * - Failure: statePath includes "*.ocrInsuranceError"
 */
export function detectOcrOutcome(
  statePath: string[],
  trace: MachineTrace,
): OcrOutcome | null {
  // Helper to check if any state in path ends with a suffix (handles nested state paths)
  const hasState = (suffix: string) =>
    statePath.some((s) => s === suffix || s.endsWith(`.${suffix}`));

  // Check for license OCR flow
  const hasLicenseOcrFlow =
    hasState("licenseFrontPhoto") || hasState("processingOcrId");

  if (hasLicenseOcrFlow) {
    // Failure: Hit error state explicitly
    // Note: saveOcrToContext is transient (has `always` transition) so it won't appear in state path
    const isFailure = hasState("ocrIdError");

    if (isFailure) {
      return {
        flow: "license",
        outcome: "failure",
        videoFile: "__tests__/ui/kiosk/bear.mjpeg",
        capturedData: null,
      };
    }
    // Success: went through OCR flow without hitting error
    // Extract the captured OCR data from context
    const capturedData = extractOcrData(trace);
    return {
      flow: "license",
      outcome: "success",
      videoFile: "__tests__/ui/kiosk/id-card.mjpeg",
      capturedData,
    };
  }

  // Check for insurance OCR flow
  const hasInsuranceOcrFlow =
    hasState("insuranceFrontPhoto") || hasState("processingOcrInsurance");

  if (hasInsuranceOcrFlow) {
    // Failure: Hit error state explicitly
    const isFailure = hasState("ocrInsuranceError");

    if (isFailure) {
      return {
        flow: "insurance",
        outcome: "failure",
        videoFile: "__tests__/ui/kiosk/bear.mjpeg",
        capturedData: null,
      };
    }
    // Success: went through OCR flow without hitting error
    // TODO: Extract insurance OCR data (different context key)

    const capturedInsuranceOcrData = extractInsuranceOcrData(trace);
    return {
      flow: "insurance",
      outcome: "success",
      videoFile: "__tests__/ui/kiosk/id-card.mjpeg",
      capturedData: capturedInsuranceOcrData,
    };
  }

  return null;
}

// ============================================================================
// Consumer Info Extraction
// ============================================================================

/**
 * Extract consumer info from trace context for isomorphic reconstruction
 * Walks through context deltas to find non-PII properties
 */
export function extractConsumerInfo(trace: MachineTrace): CapturedConsumerInfo {
  let sex: string | undefined;
  let insurancePayer: string | undefined;
  let insuranceState: string | undefined;
  let hasInsurance = false;

  // Check initial context
  const ctx = trace.initialContext as Record<string, unknown>;
  if (ctx.sex) sex = String(ctx.sex);
  if (ctx.insurancePayer) insurancePayer = String(ctx.insurancePayer);
  if (ctx.insuranceState) insuranceState = String(ctx.insuranceState);

  // Walk through deltas to find updates
  for (const event of trace.events) {
    if (event.contextDelta) {
      const { added, changed } = event.contextDelta;

      // Check added
      if (added.sex) sex = String(added.sex);
      if (added.insurancePayer) insurancePayer = String(added.insurancePayer);
      if (added.insuranceState) insuranceState = String(added.insuranceState);
      if (added.primaryInsurance || added.memberId) hasInsurance = true;

      // Check changed
      if (changed.sex) sex = String(changed.sex.to);
      if (changed.insurancePayer)
        insurancePayer = String(changed.insurancePayer.to);
      if (changed.insuranceState)
        insuranceState = String(changed.insuranceState.to);
    }
  }

  return { sex, hasInsurance, insurancePayer, insuranceState };
}

/**
 * Extract consumer name from trace context for logging
 * Walks through context deltas to find firstName and lastName
 */
export function extractConsumerName(trace: MachineTrace): string | null {
  let firstName: string | null = null;
  let lastName: string | null = null;

  // Check initial context
  const ctx = trace.initialContext as Record<string, unknown>;
  if (ctx.firstName) firstName = String(ctx.firstName);
  if (ctx.lastName) lastName = String(ctx.lastName);

  // Walk through deltas to find updates
  for (const event of trace.events) {
    if (event.contextDelta) {
      const { added, changed } = event.contextDelta;

      // Check added
      if (added.firstName) firstName = String(added.firstName);
      if (added.lastName) lastName = String(added.lastName);

      // Check changed
      if (changed.firstName) firstName = String(changed.firstName.to);
      if (changed.lastName) lastName = String(changed.lastName.to);
    }
  }

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  return null;
}

// ============================================================================
// Flow Outcome Extraction
// ============================================================================

/**
 * Extract flow outcome from trace context for database verification
 * Walks through context deltas to find treatment, pricing, and payment info
 */
export function extractFlowOutcome(trace: MachineTrace): FlowOutcome {
  // Use final reconstructed context - much more efficient than walking deltas
  const ctx = reconstructFinalContext(trace);

  // Extract treatment name from appointment response (most reliable source)
  // Shape: appointment.data.boughtTreatmentWithCharges[0].name
  let treatmentName: string | null = null;
  const appointment = ctx.appointment as {
    data?: {
      boughtTreatmentWithCharges?: Array<{ name?: string; price?: number }>;
    };
  } | null;
  if (appointment?.data?.boughtTreatmentWithCharges?.[0]?.name) {
    treatmentName = appointment.data.boughtTreatmentWithCharges[0].name;
  }

  // Extract price - prefer currentPrice, fallback to appointment data
  let price: number | null = null;
  if (ctx.currentPrice !== undefined && ctx.currentPrice !== null) {
    price = Number(ctx.currentPrice);
  } else if (appointment?.data?.boughtTreatmentWithCharges?.[0]?.price) {
    price = Number(appointment.data.boughtTreatmentWithCharges[0].price);
  }

  // Extract payment status
  const paymentSucceeded = Boolean(
    ctx.paymentStatus === "SUCCESS" || ctx.paymentSucceeded,
  );

  return { treatmentName, price, paymentSucceeded };
}

/**
 * Extract intake responses from trace context for test data setup
 * Reads from final context's `responses` field
 */
export function extractIntakeResponses(
  trace: MachineTrace,
): CapturedIntakeResponses {
  const ctx = reconstructFinalContext(trace);
  const responses = ctx.responses as CapturedIntakeResponses | undefined;
  return responses || {};
}
